@if (projectService.currentProject()) {
  <aside class="sidebar" [class.sidebar--collapsed]="collapsed" *appI18nScopeReady>
    <nav class="sidebar__nav">
      <ul class="sidebar__list">
        @for (item of navItems; track item.route) {
          <ng-template #navItemContent>
            <li
              class="sidebar__item"
              [class.sidebar__item--active]="item.route === activeRoute()"
              [class.disabled]="isNavItemDisabled(item)"
            >
              <a
                [tabindex]="0"
                class="sidebar__link"
                (click)="navigateTo(item)"
                (keydown.enter)="navigateTo(item)"
                [matTooltip]="item.requiresProject && !projectService.currentProject() ? ('no_project_selected_tooltip' | i18n) : ''"
                matTooltipPosition="right"
              >
                @if (item.symbolIcon) {<span class="sidebar__icon material-symbols-outlined">{{ item.symbolIcon }}</span>}
                @else if (item.icon) {<mat-icon class="sidebar__icon">{{ item.icon }}</mat-icon>}
                <span class="sidebar__label">{{ item.label | i18n }}</span>
              </a>
              @if (DASHBOARD_ROUTES.includes(item.route) && !collapsed) {
              <div
                class="flex-column"
                cdkDropList
                [cdkDropListData]="dashboardsPerRoute()[item.route]"
                [cdkDropListSortingDisabled]="item.route !== activeRoute()"
                (cdkDropListDropped)="onDashboardDrop($event, item.route)"
              >
                @for (dashboard of (dashboardsPerRoute()[item.route]); track dashboard.id) {
                  <div
                    class="dashboard-item"
                    [tabindex]="0"
                    cdkDrag
                    [cdkDragDisabled]="item.route !== activeRoute()"
                    (click)="navigateTo(item, dashboard.name)"
                    (keydown.enter)="navigateTo(item, dashboard.name)"
                    [class.active]="dashboard.name === activeSubroute"
                  >
                    <span class="dot"></span>
                    <span class="label">{{dashboard.name}}</span>
                  </div>
                }

                @if (item.route === activeRoute() && item.editPermission) {
                <div class="dashboard-item add"
                     [tabindex]=0
                     (keydown.enter)="addDashboard()"
                     (click)="addDashboard()"
                     *appHasPermission="item.editPermission"
                >
                  <span class="plus">+</span>
                </div>
                } @else if (item.route === activeRoute() && !item.editPermission) {
                <div class="dashboard-item add" [tabindex]=0 (keydown.enter)="addDashboard()" (click)="addDashboard()">
                  <span class="plus">+</span>
                </div>
                }
              </div>
              }

              @if (item.route === REPORT_ROUTE && !collapsed) {
              <div class="flex-column">
                @for (report of reportsForSidebar(); track report.id) {
                  <div class="dashboard-item" [tabindex]="0"
                    (click)="navigateTo(item, report.id)"
                    (keydown.enter)="navigateTo(item, report.id)"
                    [class.active]="report.id === activeSubroute"
                  >
                    <span class="dot"></span>
                    <span class="label">{{report.name}}</span>
                  </div>
                }

                @if (item.route === activeRoute()) {
                <div class="dashboard-item add" [tabindex]="0" (keydown.enter)="addReport()" (click)="addReport()">
                  <span class="plus">+</span>
                </div>
                }
              </div>
              }
            </li>
          </ng-template>

          @if (item.viewPermission) {
            <ng-container *appHasPermission="item.viewPermission">
              <ng-container *ngTemplateOutlet="navItemContent" />
            </ng-container>
          } @else {
            <ng-container *ngTemplateOutlet="navItemContent" />
          }
        }

        <!-- Data Management expandable group -->
        <ng-container *appHasPermission="Permissions.VIEW_MANAGED_DATA">
        <li class="sidebar__item sidebar__item--group"
            [class.sidebar__item--group-active]="dataManagementExpanded() || dataManagementRouteActive()">
          <a
            [tabindex]="0"
            class="sidebar__link sidebar__link--group"
            (click)="dataManagementExpanded.set(!dataManagementExpanded())"
            (keydown.enter)="dataManagementExpanded.set(!dataManagementExpanded())"
          >
            <mat-icon class="sidebar__icon">storage</mat-icon>
            <span class="sidebar__label">{{ dataManagementGroup.label | i18n }}</span>
            @if (!collapsed) {
              <mat-icon class="sidebar__chevron">
                {{ dataManagementExpanded() ? 'expand_less' : 'expand_more' }}
              </mat-icon>
            }
          </a>
          @if (dataManagementExpanded() && !collapsed) {
            <div class="sidebar__children">
              <ng-container *appHasPermission="Permissions.EDIT_MANAGED_DATA">
                <div
                  class="sidebar__child sidebar__child--settings"
                  [tabindex]="0"
                  (click)="openSectionManager()"
                  (keydown.enter)="openSectionManager()"
                >
                  <mat-icon class="sidebar__child-icon">settings</mat-icon>
                  <span class="label">{{ 'manage_sections_entry' | i18n }}</span>
                </div>
              </ng-container>
              @for (child of dataManagementChildren(); track child.route) {
                @if (child.permission) {
                  <div
                    class="sidebar__child"
                    [class.sidebar__child--active]="isChildActive(child)"
                    [tabindex]="0"
                    (click)="navigateToChild(child)"
                    (keydown.enter)="navigateToChild(child)"
                    *appHasPermission="child.permission"
                  >
                    <span class="dot"></span>
                    <span class="label">@if (child.isRawLabel) { {{ child.label }} } @else { {{ child.label | i18n }} }</span>
                  </div>
                } @else {
                  <div
                    class="sidebar__child"
                    [class.sidebar__child--active]="isChildActive(child)"
                    [tabindex]="0"
                    (click)="navigateToChild(child)"
                    (keydown.enter)="navigateToChild(child)"
                  >
                    <span class="dot"></span>
                    <span class="label">@if (child.isRawLabel) { {{ child.label }} } @else { {{ child.label | i18n }} }</span>
                  </div>
                }
              }
            </div>
          }
        </li>
        </ng-container>

        <!-- TimeSeries Data expandable group -->
        <ng-container *appHasPermission="Permissions.VIEW_TIMESERIES">
        <li class="sidebar__item sidebar__item--group"
            [class.sidebar__item--group-active]="timeseriesExpanded() || timeseriesRouteActive()">
          <a
            [tabindex]="0"
            class="sidebar__link sidebar__link--group"
            (click)="timeseriesExpanded.set(!timeseriesExpanded())"
            (keydown.enter)="timeseriesExpanded.set(!timeseriesExpanded())"
          >
            <mat-icon class="sidebar__icon">{{ timeseriesDataGroup().icon }}</mat-icon>
            <span class="sidebar__label">{{ timeseriesDataGroup().label | i18n }}</span>
            @if (!collapsed) {
              <mat-icon class="sidebar__chevron">
                {{ timeseriesExpanded() ? 'expand_less' : 'expand_more' }}
              </mat-icon>
            }
          </a>
          @if (timeseriesExpanded() && !collapsed) {
            <div class="sidebar__children">
              <ng-container *appHasPermission="Permissions.EDIT_TIMESERIES">
                <div
                  class="sidebar__child sidebar__child--settings"
                  [tabindex]="0"
                  (click)="openTimeseriesSectionManager()"
                  (keydown.enter)="openTimeseriesSectionManager()"
                >
                  <mat-icon class="sidebar__child-icon">settings</mat-icon>
                  <span class="label">{{ 'manage_sections_entry' | i18n }}</span>
                </div>
              </ng-container>
              @for (child of timeseriesDataGroup().children; track child.route) {
                @if (child.permission) {
                  <div
                    class="sidebar__child"
                    [class.sidebar__child--active]="isChildActive(child)"
                    [tabindex]="0"
                    (click)="navigateToChild(child)"
                    (keydown.enter)="navigateToChild(child)"
                    *appHasPermission="child.permission"
                  >
                    <span class="dot"></span>
                    <span class="label">@if (child.isRawLabel) { {{ child.label }} } @else { {{ child.label | i18n }} }</span>
                  </div>
                } @else {
                  <div
                    class="sidebar__child"
                    [class.sidebar__child--active]="isChildActive(child)"
                    [tabindex]="0"
                    (click)="navigateToChild(child)"
                    (keydown.enter)="navigateToChild(child)"
                  >
                    <span class="dot"></span>
                    <span class="label">@if (child.isRawLabel) { {{ child.label }} } @else { {{ child.label | i18n }} }</span>
                  </div>
                }
              }
              <ng-container *appHasPermission="Permissions.EDIT_TIMESERIES">
              <div class="dashboard-item add"
                   [tabindex]="0"
                   (click)="addTimeSeriesTable()"
                   (keydown.enter)="addTimeSeriesTable()"
              >
                <span class="plus">+</span>
              </div>
              </ng-container>
            </div>
          }
        </li>
        </ng-container>

        <!-- Weighbridge expandable group -->
        <ng-container *appHasPermission="Permissions.VIEW_WEIGHBRIDGE">
        <li class="sidebar__item sidebar__item--group sidebar__item--weighbridge-active">
          <a
            [tabindex]="0"
            class="sidebar__link sidebar__link--group"
            (click)="weighbridgeExpanded.set(!weighbridgeExpanded())"
            (keydown.enter)="weighbridgeExpanded.set(!weighbridgeExpanded())"
          >
            <span class="sidebar__icon material-symbols-outlined">scale</span>
            <span class="sidebar__label">{{ weighbridgeGroup.label | i18n }}</span>
            @if (!collapsed) {
              <mat-icon class="sidebar__chevron">
                {{ weighbridgeExpanded() ? 'expand_less' : 'expand_more' }}
              </mat-icon>
            }
          </a>
          @if (weighbridgeExpanded() && !collapsed) {
            <div class="sidebar__children">
              @for (child of weighbridgeGroup.children; track child.route) {
                @if (child.permission) {
                  <div
                    class="sidebar__child sidebar__child--purple"
                    [class.sidebar__child--active-purple]="isChildActive(child)"
                    [tabindex]="0"
                    (click)="navigateToChild(child)"
                    (keydown.enter)="navigateToChild(child)"
                    *appHasPermission="child.permission"
                  >
                    <span class="dot"></span>
                    <span class="label">@if (child.isRawLabel) { {{ child.label }} } @else { {{ child.label | i18n }} }</span>
                  </div>
                } @else {
                  <div
                    class="sidebar__child sidebar__child--purple"
                    [class.sidebar__child--active-purple]="isChildActive(child)"
                    [tabindex]="0"
                    (click)="navigateToChild(child)"
                    (keydown.enter)="navigateToChild(child)"
                  >
                    <span class="dot"></span>
                    <span class="label">@if (child.isRawLabel) { {{ child.label }} } @else { {{ child.label | i18n }} }</span>
                  </div>
                }
              }
            </div>
          }
        </li>
        </ng-container>
      </ul>
    </nav>
  </aside>
}
